import { getRequestErrorMessages, isServerError } from 'global/helpers/requestError'
import { Fragment, type ReactNode } from 'react'
import { type ErrorOption, type GlobalError, type ValidateResult } from 'react-hook-form'

export const handleFormSubmission = async (
  handler: () => Promise<unknown>,
  options: {
    onSuccess?: () => void
    onError?: (error: unknown) => void
  },
): Promise<void> => {
  try {
    await handler()
  } catch (error) {
    options.onError?.(error)
    // swallow 4xx errors, so they are not reported to Sentry
    if (isServerError(error) && error.response.status >= 400  && error.response.status < 500) {
      return
    }
    throw error
  }
  options.onSuccess?.()
}

export const processServerErrorMessages = (error: unknown): Record<string, GlobalError> => {
  return getRequestErrorMessages(error).reduce((errors: Record<string, GlobalError>, errorMessage, index) => {
    errors[`server_${index + 1}`] = { message: errorMessage.message }
    return errors
  }, {})
}

export const getGlobalErrorMessages = (errors?: Record<string, GlobalError>): ReactNode => {
  if (errors) {
    const messages: string[] = []
    for (const [key, value] of Object.entries(errors)) {
      if (key === 'ref' || !value.message) {
        continue
      }
      messages.push(value.message)
    }
    return messages.map((message, index) => (
      // don't care about stability here
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={index}>
        {index > 0 && <br />}
        {message}
      </Fragment>
    ))
  }
  return undefined
}

export const getFieldErrorMessages = (error?: ErrorOption): ReactNode => {
  if (error?.types) {
    const messages: ValidateResult[] = []
    Object.values(error.types).forEach((error) => messages.push(error))
    return messages.map((message, index) => (
      // don't care about stability here
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={index}>
        {index > 0 && <br />}
        {message}
      </Fragment>
    ))
  } else if (error?.message) {
    return error.message
  }
  return undefined
}

export const cleanupRequestValues = (values: Record<string, any> | any[]): Record<string, any> | any[] => {
  if (Array.isArray(values)) {
    // If input is an array, map over the elements and apply cleanup
    return values.map((value) => {
      const cleanedValue = typeof value === 'string' ? value.trim() : value
      return typeof cleanedValue === 'boolean' || cleanedValue ? cleanedValue : null
    })
  } else {
    // If input is an object, iterate over key-value pairs and apply cleanup
    const processedValues: Record<string, any> = {}
    for (const [key, value] of Object.entries(values)) {
      const cleanedValue = typeof value === 'string' ? value.trim() : value
      processedValues[key] = typeof cleanedValue === 'boolean' || cleanedValue ? cleanedValue : null
    }
    return processedValues
  }
}

export const processDefaultValues = (
  values: Record<string, any>,
  overrides: Record<string, any> = {},
): Record<string, any> => {
  const processedValues: Record<string, any> = {}
  for (const [key, value] of Object.entries(values)) {
    processedValues[key] = value ?? (Object.hasOwn(overrides, key) ? overrides[key] : '')
  }
  return processedValues
}
